'use client';

import { useBunja } from 'bunja/react';
import { keyBy } from 'es-toolkit';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import { Styles, css } from '@flipster/styled-system/css';
import { grid, hstack, stack } from '@flipster/styled-system/patterns';

import { ImgOrLottie } from '../assets';
import { rollAndMoveBunja } from './state';

interface Props {
  css?: Styles;
}

export const Rewards = ({ css: cssProps }: Props) => {
  const { rollAndDiceSectionLayout, rewardsAtom } = useBunja(rollAndMoveBunja);
  const rewards = useAtomValue(rewardsAtom);

  const prizesKeyById = useMemo(() => {
    if (rollAndDiceSectionLayout == null) return {};
    const prizes = [
      ...(rollAndDiceSectionLayout.prizesCollection?.items ?? []),
      ...(rollAndDiceSectionLayout.goalPrizesCollection?.items ?? []),
    ];
    return keyBy(prizes, (i) => i?.id ?? 'unknown');
  }, [rollAndDiceSectionLayout]);

  if (!rewards.length) return null;

  return (
    <div
      className={css(
        stack.raw({
          gap: 10,
          p: 20,
          mt: 40,
          rounded: 16,
        }),
        cssProps,
      )}
    >
      <p
        className={css({
          textStyle: 'Title/XS',
          color: 'content.default.level-5',
          textAlign: 'left',
        })}
      >
        <FormattedMessage id='Campaign.r251124.Your_rewards_colon' />
      </p>
      <ul
        className={grid({
          gridTemplateColumns: { base: 'repeat(2, 1fr)', tablet: 'repeat(auto-fill, minmax(156px, max-content))' },
          gap: 8,
        })}
      >
        {rewards.map((reward) => {
          const prize = prizesKeyById[reward.id];
          if (!prize) return null;
          return (
            <li
              className={hstack({
                bg: 'background.default.level-3',
                pl: 4,
                pr: 10,
                py: 8,
                rounded: 8,
                overflow: 'hidden',
                h: 100,
              })}
              key={reward.id}
            >
              <ImgOrLottie url={prize.image?.url} alt={prize.name ?? 'reward'} css={{ w: 80, h: 80, flexShrink: 0 }} />
              <div className={stack({ gap: 4 })}>
                {prize.name && (
                  <p
                    className={css({
                      textStyle: 'Text/S/Regular',
                      color: 'content.default.level-5',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineClamp: 3,
                    })}
                  >
                    {prize.name}
                  </p>
                )}
                <Badge css={{ w: 'fit-content' }} type='tint' color='promotion'>{`x${reward.count}`}</Badge>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
